/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { OrderSummary } from '@msdyn365-commerce-modules/order-summary-utilities';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import get from 'lodash/get';
import * as React from 'react';

import { IDlvryCheckoutV2Data } from '../dlvry-checkout-v2.data';
import { IDlvryCheckoutV2Props, IDlvryCheckoutV2Resources } from '../dlvry-checkout-v2.props.autogenerated';
import { Cart, CartLine } from '@msdyn365-commerce/retail-proxy';
import { ICheckoutState } from '../dlvry-checkout-v2';
import { IOrderSummaryLinesExt } from '../../../shared-utils/types';

export interface IOrderSummary {
    children?: React.ReactNode;
    orderSummaryProps: INodeProps;
    heading?: React.ReactNode;
    lines?: IOrderSummaryLinesExt;
    isCalculatingTotal?: boolean;
    resources: IDlvryCheckoutV2Resources;
}
export const getOrderSummary = (input: IDlvryCheckoutV2Props<IDlvryCheckoutV2Data>, state: ICheckoutState): IOrderSummary => {
    const {
        config: { orderSummaryHeading },
        data: { checkout },
        resources: {
            subTotalLabel,
            shippingLabel,
            taxLabel,
            orderTotalLabel,
            loyaltyLabel,
            giftcardLabel,
            totalDiscountsLabel,
            freeText,
            toBeCalculatedText,
            otherCharges,
            customerAccountLabel
        },
        context,
        typeName,
        id,
        telemetry
    } = input;

    const checkoutState = get(checkout, 'result');
    let fixedCart = { ...checkout.result?.checkoutCart.cart } as Cart;
    const showLineItems = fixedCart?.CartLines && fixedCart?.CartLines.length > 0;
    const channelConfiguration = context.request.channel!;
    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    const { channelDeliveryOptionConfig, featureState } = input.data;
    const retailMultiplePickUpOptionEnabled = featureState?.result?.find(feature => feature.Name === multiplePickupStoreSwitchName)
        ?.IsEnabled;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
    const isShowOrHideTaxEnabled = context.request.app.config.isEnableShowOrHideSalesTaxECommerceEnabled === true;

    const handleOrderSummaryHeadingChange = (event: Msdyn365.ContentEditableEvent) => {
        const {
            config: { orderSummaryHeading }
        } = input;
        if (orderSummaryHeading) {
            orderSummaryHeading.text = event.target.value;
        }
    };

    // Use price from cart if price has previously been calculated. Otherwise add cart line prices together "manually"
    if (!state.hasCalculatedTotal && fixedCart?.CartLines) {
        const sumPrice = fixedCart?.CartLines
            .map((x: CartLine) => x.NetAmountWithoutTax
                ? x.NetAmountWithoutTax
                : x.Price! * (x.Quantity || 1)
            )
            .reduce((sum: number, price: number) => sum + price, 0);

        // Reset some values to avoid confusion with "Free" labels when price hasn't been calculated yet
        fixedCart = {
            ...fixedCart,
            DiscountAmount: 0,
            DiscountAmountWithoutTax: 0,
            SubtotalAmount: sumPrice,
            SubtotalAmountWithoutTax: sumPrice,
            TaxAmount: undefined,
            TotalAmount: undefined
        };
    }

    fixedCart.ShippingChargeAmount = undefined;
    fixedCart.OtherChargeAmount = undefined;

    const lines: IOrderSummaryLinesExt | undefined = showLineItems
        ? OrderSummary({
            checkoutState,
            cart: fixedCart,
            subTotalLabel,
            shippingLabel,
            taxLabel,
            orderTotalLabel,
            loyaltyLabel,
            giftcardLabel,
            totalDiscountsLabel,
            freeText,
            toBeCalculatedText,
            context,
            typeName,
            id,
            channelConfiguration,
            telemetry,
            otherChargeLabel: otherCharges,
            retailMultiplePickUpOptionEnabled,
            channelDeliveryOptionConfig: channelDeliveryOptionConfig?.result,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- existing property.
            isTaxIncludedInPrice: fixedCart.IsTaxIncludedInPrice,
            isShowTaxBreakup: isShowOrHideTaxEnabled,
            customerAccountLabel
        })
        : undefined;

    return {
        orderSummaryProps: { className: 'ms-checkout-guest-profile__selected-item' },
        heading: orderSummaryHeading && orderSummaryHeading.text && (
            <Msdyn365.Text
                className='msc-order-summary__heading'
                {...orderSummaryHeading}
                tag={orderSummaryHeading.tag || 'h2'}
                text={orderSummaryHeading.text}
                editProps={{ onEdit: handleOrderSummaryHeadingChange, requestContext: context.request }}
            />
        ),
        resources: input.resources,
        lines
    };
};
